import { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useTranslation } from "react-i18next";
import { NotificationsNone, PriorityHigh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Notification } from "./types";
import { useStore } from "@/zustand/store";
import { useUpdateNotification } from "./api/updateNotification";

export const NotificationButton = () => {
  const { t } = useTranslation();
  const notifications: Notification[] = useStore(
    (state) => state.notifications
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const { mutateAsync: updateNotification } = useUpdateNotification();

  const uniqueNotifications = Array.from(
    notifications
      .reduce((map, notification) => {
        if (
          !notification.chatMessage.leadId ||
          !map.has(notification.chatMessage.leadId) ||
          new Date(notification.chatMessage.sentAt) >
            new Date(
              map.get(notification.chatMessage.leadId)!.chatMessage.sentAt
            )
        ) {
          map.set(notification.chatMessage.leadId, notification);
        }
        return map;
      }, new Map<number, Notification>())
      .values()
  );

  const unseenCount = uniqueNotifications.filter(
    (notification) => !notification.readAt
  ).length;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = async (notification: Notification) => {
    if (notification.chatMessage.messageId) {
      try {
        await updateNotification({
          leadId: notification.chatMessage.leadId,
          data: { readAt: new Date().toISOString() },
        });

        useStore.setState({
          notifications: notifications.map((notif) =>
            notif.chatMessage.messageId === notification.chatMessage.messageId
              ? { ...notif, readAt: new Date().toISOString() }
              : notif
          ),
        });

        setAnchorEl(null);

        if (notification.chatMessage.leadId) {
          navigate(`/leads/${notification.chatMessage.leadId}`);
        }
      } catch (error) {
        console.error("Error updating notification", error);
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Tooltip title={<>{t("dropdownNavigation.notifications")}</>}>
        <IconButton onClick={handleClick} color="inherit" size="large">
          <Badge
            badgeContent={unseenCount}
            color="error"
            invisible={unseenCount === 0}
            overlap="circular"
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: "250px",
          },
        }}
      >
        {uniqueNotifications.length > 0 ? (
          uniqueNotifications.map((notification) => (
            <MenuItem
              key={notification.chatMessage.messageId}
              onClick={() => handleClose(notification)}
              sx={(theme) => ({
                backgroundColor: notification.readAt
                  ? theme.palette.mode === "light"
                    ? "grey.100"
                    : "grey.700"
                  : "inherit",
                display: "flex",
                flexDirection: "column",
                gap: 0.4,
                padding: "0.5rem 1rem",
                alignItems: "flex-start",
              })}
            >
              <Typography
                variant="body1"
                color="text.primary"
                sx={{
                  fontWeight: notification.readAt ? "normal" : "bold",
                  display: "inline-flex",
                  alignItems: "center",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("lead.columns.leadId")}:{" "}
                {notification.chatMessage.leadId || t("lead.unknown")}
                {!notification.readAt && (
                  <PriorityHigh
                    color="error"
                    fontSize="small"
                    sx={{ marginLeft: "8px" }}
                  />
                )}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  overflow: "hidden",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color={
                    notification.readAt ? "text.secondary" : "text.primary"
                  }
                  sx={{
                    flex: 1,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {notification.chatMessage.userPerson?.name || "Unknown"}{" "}
                  {t("lead.messaged")}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Box
              marginTop="-0.2rem"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              <NotificationsNone fontSize="large" color="disabled" />
              <Typography variant="body1" color="text.secondary" align="center">
                {t("dropdownNavigation.noNotifications")}
              </Typography>
            </Box>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
